import { Injectable } from "@angular/core";

import { Observable, of } from "rxjs";
import { first, map, switchMap } from "rxjs/operators";

import { executeWithProgress } from "@dffedb/rxjs";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { EjendomsfaktorDefinition, EjendomsfaktorDefinitionDto } from "@e-forsyning/common/ejendomsfaktor";
import { BrugerHttpClient, ParametreForAktuelleInstallation, UmbracoHttpClient } from "@e-forsyning/common/http";

import { OpgaveListeItem } from "../opgave-liste/model/opgave-liste-item.model";
import { KomponentType } from "../opgave/arbejdsgang/komponenter/model/komponent-type.model";
import { Opgave } from "../opgave/model/opgave.model";
import { OpgavestyringIndstillingerDto } from "../opgave/model/opgavestyring-indstillinger.dto";
import { OpgavestyringIndstillinger } from "../opgave/model/opgavestyring-indstillinger.model";
import { OpgaveMapper } from "./opgave.mapper";

@Injectable({ providedIn: "root" })
export class OpgaveRepository {
    constructor(
        private readonly http: BrugerHttpClient,
        private readonly umbracoHttp: UmbracoHttpClient,
        private readonly installationCredentials: AppserverInstallationCredentialsRepository,
        private readonly mapper: OpgaveMapper
    ) {}

    public hentIndstillinger(): Observable<OpgavestyringIndstillinger> {
        return this.umbracoHttp.hentSideData<OpgavestyringIndstillingerDto>("opgaver").pipe(
            map((dto) => ({
                sideTitel: dto.menuTekst
            }))
        );
    }

    public hentKomponentTyper(): Observable<KomponentType[]> {
        return this.http.get("api/HentServicerapportKomponenttyper").pipe(map((data: any) => data.komponenttyper as KomponentType[]));
    }

    public hentOpgaver(): Observable<OpgaveListeItem[]> {
        const installation = this.installationCredentials.hent();
        return this.http.get(`api/HentServicerapportOpgaver/inr/${installation.installationNr}`).pipe(
            map((data: any) => {
                const result = data.opgaver as OpgaveListeItem[];
                return result.filter((a) => a.aktiv);
            })
        );
    }

    public hentOpgave(opgaveId: number): Observable<Opgave> {
        const installation = this.installationCredentials.hent();
        return this.http
            .get(`api/HentServicerapportOpgave/opgaveid/${opgaveId}/unr/${installation.ejendomNr}/inr/${installation.installationNr}`)
            .pipe(
                first(),
                switchMap((data) => this.mapper.dtoToOpgave(data))
            );
    }

    public udfoerOpgave(opgave: Opgave, onProgress: (value: number) => void): Promise<void> {
        const data = this.mapper.opgaveToDto(opgave);
        return executeWithProgress(
            () =>
                this.http.post("api/GemServicerapportOpgave", data, ParametreForAktuelleInstallation.false, {
                    observe: "events",
                    reportProgress: true
                }),
            () => of(null),
            onProgress
        );
    }

    public hentEjendomsfaktorDefinitioner(): Observable<EjendomsfaktorDefinition[]> {
        const result = this.http
            .get<{ ejendomsfaktordefinitioner: EjendomsfaktorDefinitionDto[] }>("api/HentServicerapportEjendomsfaktorDefinitioner")
            .pipe(map((dto) => this.mapper.dtoToEjendomsfaktorDefinitioner(dto.ejendomsfaktordefinitioner)));
        return result;
    }
}
